import { IEnv } from '../../../infrastructure/i-env';
import { ICurrentUser } from '../../../infrastructure/i-current-user';
import { Language } from '../../../dto/language';
import { getNewsSourceLanguage, getNewsTargetLanguage } from '../../../dto/user-preferences-dto';
import { TranslationMode } from '../../../dto/translation-mode';
import { CategoryDto, DefaultNewsCategoryId } from '../../../dto/categories';
import { NewsTranslationContentResponse, SentenceTranslationData } from '../../../dto/news-content';
import { RecallStatus } from '../../../dto/user-words';
import { NewsCategoryStore } from '../../../stores/news-category-store';
import { NewsTranslationStore } from '../../../stores/news-translation-store';
import { IUserFsrsCardService } from '../../../services/i-user-fsrs-card-service';
import { IQuizzService } from '../../../services/i-quizz-service';
import { IMnemonicService } from '../../../services/i-mnemonic-service';
import { IPremiumTrackingService } from '../../../services/i-premium-tracking-service';
import { QuizViewModel } from '../../main/quiz-view-model/quiz-view-model';
import { QuizPopupViewModel } from '../../main/quiz-popup-view-model/quiz-popup-view-model';
import { convertQuizStepsToQuizWords } from '../../quizz/quiz-helpers';
import { PaywallPopupViewModel } from '../../popups/paywall-popup.view-model';
import { MnemonicPopupItem, MnemonicPopupViewModel } from '../../popups/mnemonic-popup.view-model';
import { TextSplitter } from './text-splitter';

export interface ArticleWordPair {
	original: string;
	transliteration?: string;
	translation: string;
}

interface ArticlePartWord {
	wordId: string;
	word: string;
	transliteration?: string;
	translation: string;
	normalizedWord?: string;
	normalizedTranslation?: string;
}

export type ArticleLoadingKind = 'categories' | 'article' | 'none';

export interface ArticlePageState {
	nativeLanguage: Language;
	learnedLanguage: Language;
	mode: TranslationMode;
	routeCategoryId?: string;
	translationId?: string;

	categories: CategoryDto[];
	article: NewsTranslationContentResponse | null;
	selectedCategoryId: string;
	categoryTitleForHeader: string;

	translate: boolean;
	transliterate: boolean;

	isLoadingCategories: boolean;
	isLoadingArticle: boolean;
	isLoading: boolean;
	loadingKind: ArticleLoadingKind;
	error: string;

	showWordView: boolean;
	allWords: ArticleWordPair[];
	articleParts: SentenceTranslationData[][];
	visiblePartCount: number;
	isLoadingMnemonics: boolean;
	isMnemonicPopupVisible: boolean;

	isNavigatingToPrevious: boolean;
	isNavigatingToNext: boolean;
	hasPrevious: boolean;
	hasNext: boolean;
}

export abstract class IArticlePageViewModel {
	abstract readonly state: ArticlePageState;

	abstract closeWordView(): void;

	abstract onReady(): Promise<void>;

	abstract toggleTranslate(): void;

	abstract toggleTransliterate(): void;

	abstract canShowAllWords(): boolean;

	abstract showAllWords(): void;

	abstract getArticleBlocks(): SentenceTranslationData[][];

	abstract getVisibleContent(): SentenceTranslationData[];

	abstract canContinueReading(): boolean;

	abstract continueReading(): Promise<void>;

	abstract canPlayQuiz(): boolean;

	abstract startQuiz(): Promise<void>;

	abstract openMnemonicPopupForPart(partIndex: number): Promise<void>;

	abstract openMnemonicPopupForCurrentPart(): Promise<void>;

	abstract navigateToPrevious(): Promise<void>;

	abstract navigateToNext(): Promise<void>;

	abstract navigateToWordCollection(): Promise<void>;

	abstract navigateToRepeat(): Promise<void>;
}

export class ArticlePageViewModel extends IArticlePageViewModel {
	public readonly state: ArticlePageState;
	public useTelegramPayment = false;
	private readonly textSplitter = new TextSplitter();
	private categoryStore: NewsCategoryStore | null = null;
	private translationStore: NewsTranslationStore | null = null;
	private isReady = false;
	private paywallShownForTranslationId: string | null = null;
	private mnemonicFlowToken = 0;

	constructor(
			private readonly env: IEnv,
			private readonly currentUser: ICurrentUser,
			private readonly getCategoryStore: () => Promise<NewsCategoryStore>,
			private readonly getTranslationStore: () => Promise<NewsTranslationStore>,
			initialNativeLanguage: Language,
			initialLearnedLanguage: Language,
			initialMode: TranslationMode
	) {
		super();

		this.state = env.reactive({
			nativeLanguage: initialNativeLanguage,
			learnedLanguage: initialLearnedLanguage,
			mode: initialMode,
			routeCategoryId: undefined,
			translationId: undefined,

			categories: [],
			article: null,
			selectedCategoryId: '',
			categoryTitleForHeader: '',

			translate: false,
			transliterate: false,

			isLoadingCategories: false,
			isLoadingArticle: false,
			isLoading: false,
			loadingKind: 'none' as ArticleLoadingKind,
			error: '',

			showWordView: false,
			allWords: [],
			articleParts: [],
			visiblePartCount: 0,
			isLoadingMnemonics: false,
			isMnemonicPopupVisible: false,

			isNavigatingToPrevious: false,
			isNavigatingToNext: false,
			hasPrevious: false,
			hasNext: false
		}) as ArticlePageState;

		this.setupRouteWatches();
	}

	closeWordView(): void {
		this.state.showWordView = false;
	}

	private resolveNewsLanguages(): { sourceLang: Language; targetLang: Language } {
		return {
			sourceLang: getNewsSourceLanguage({
				nativeLanguage: this.state.nativeLanguage,
				learnedLanguage: this.state.learnedLanguage,
				mode: this.state.mode
			}),
			targetLang: getNewsTargetLanguage({
				nativeLanguage: this.state.nativeLanguage,
				learnedLanguage: this.state.learnedLanguage,
				mode: this.state.mode
			})
		};
	}

	async onReady(): Promise<void> {
		this.isReady = true;
		await this.reloadAll();
	}

	toggleTranslate(): void {
		this.state.translate = !this.state.translate;
	}

	toggleTransliterate(): void {
		this.state.transliterate = !this.state.transliterate;
	}

	canShowAllWords(): boolean {
		return this.state.mode===TranslationMode.Partial;
	}

	showAllWords(): void {
		if (!this.state.article) return;
		this.state.allWords = this.extractAllWords(this.state.article.content);
		if (this.state.allWords.length > 0) {
			this.state.showWordView = true;
		}
	}

	getArticleBlocks(): SentenceTranslationData[][] {
		return this.state.articleParts;
	}

	getVisibleContent(): SentenceTranslationData[] {
		return this.state.articleParts.flatMap((part) => part);
	}

	canContinueReading(): boolean {
		return false;
	}

	async continueReading(): Promise<void> {
		return;
	}

	canPlayQuiz(): boolean {
		if (!this.state.article) {
			return false;
		}
		return !this.state.isLoadingMnemonics && !this.state.isMnemonicPopupVisible;
	}

	async openMnemonicPopupForPart(partIndex: number): Promise<void> {
		await this.showMnemonicPopupForPart(partIndex, this.mnemonicFlowToken);
	}

	async openMnemonicPopupForCurrentPart(): Promise<void> {
		await this.openMnemonicPopupForPart(0);
	}

	async startQuiz(): Promise<void> {
		if (!this.state.article || !this.canPlayQuiz()) return;

		try {
			await this.currentUser.initialize();
			const userId = this.currentUser.getUserId() ?? undefined;

			const translationId = this.state.translationId;
			if (!translationId) return;

			const quizzService = this.env.get(IQuizzService);
			const quizViewModel = new QuizViewModel(
					quizzService,
					userId,
					this.state.nativeLanguage,
					this.state.learnedLanguage,
					'article',
					translationId,
					7
			);

			await quizViewModel.loadQuiz();
			if (quizViewModel.steps.length > 0) {
				const quizWords = convertQuizStepsToQuizWords(quizViewModel.steps);
				const popupService = this.env.popupService;

				const popupVm = new QuizPopupViewModel(this.env, quizWords,
						this.state.nativeLanguage,
						this.state.learnedLanguage,
						async (payload) => {
							await this.handleQuizAnswer(payload);
						});

				void popupService.showPopup(popupVm).catch((e) => this.env.log('Error showing quiz popup', e));
			}
		} catch (e) {
			this.env.log('Error loading quiz options', e);
		}
	}

	private async handleQuizAnswer(payload: { translatedWordId: string; recallStatus: RecallStatus; isCorrect?: boolean }): Promise<void> {
		try {
			if (!payload.translatedWordId) {
				return;
			}
			await this.currentUser.initialize();
			const userId = this.currentUser.getUserId();
			if (!userId) {
				return;
			}

			const wordsService = this.env.get(IUserFsrsCardService);
			await wordsService.recordQuizAttempt({
				userId,
				translatedWordId: payload.translatedWordId,
				recallStatus: payload.recallStatus,
				isCorrect: payload.isCorrect,
				userTimeZoneOffset: new Date().getTimezoneOffset()
			});
		} catch (e) {
			this.env.log('Error recording quiz attempt', e);
		}
	}

	async navigateToPrevious(): Promise<void> {
		const previousId = this.state.article?.previousTranslationId;
		if (!previousId) return;

		this.state.isNavigatingToPrevious = true;
		try {
			if (!this.translationStore) {
				this.translationStore = await this.getTranslationStore();
			}
			await this.translationStore.preloadContent(previousId);

			const catId = this.state.article?.title.category || this.state.selectedCategoryId;
			await this.env.routing.navigate(
					this.env.routing.getArticleLink(this.state.nativeLanguage, this.state.learnedLanguage, this.state.mode, catId, previousId)
			);
		} finally {
			this.state.isNavigatingToPrevious = false;
		}
	}

	async navigateToNext(): Promise<void> {
		const nextId = this.state.article?.nextTranslationId;
		if (!nextId) return;

		this.state.isNavigatingToNext = true;
		try {
			if (!this.translationStore) {
				this.translationStore = await this.getTranslationStore();
			}
			await this.translationStore.preloadContent(nextId);

			const catId = this.state.article?.title.category || this.state.selectedCategoryId;
			await this.env.routing.navigate(
					this.env.routing.getArticleLink(this.state.nativeLanguage, this.state.learnedLanguage, this.state.mode, catId, nextId)
			);
		} finally {
			this.state.isNavigatingToNext = false;
		}
	}

	async navigateToWordCollection(): Promise<void> {
		if (!(await this.ensureAuthenticated())) return;
		await this.env.routing.navigate(this.env.routing.getWordCollectionLink(this.state.nativeLanguage, this.state.learnedLanguage));
	}

	async navigateToRepeat(): Promise<void> {
		if (!(await this.ensureAuthenticated())) return;
		await this.env.routing.navigate(this.env.routing.getWordRepeatLink(this.state.nativeLanguage, this.state.learnedLanguage));
	}

	private setupRouteWatches(): void {
		this.env.watchMultiple(
				[
					() => this.env.routing.getRouteParam('nativeLanguage'),
					() => this.env.routing.getRouteParam('learnedLanguage'),
					() => this.env.routing.getRouteParam('mode')
				] as const,
				() => {
					this.syncPreferencesFromRoute();
					if (this.isReady) void this.reloadAll();
				}
		);

		this.env.watch(
				() => this.env.routing.getRouteParam('categoryId'),
				() => {
					this.state.routeCategoryId = this.normalizeCategoryId(this.env.routing.getRouteParam('categoryId'));
				}
		);

		this.env.watch(
				() => this.env.routing.getRouteParam('id') || this.env.routing.getRouteParam('newsId'),
				() => {
					this.state.translationId = this.normalizeCategoryId(this.env.routing.getRouteParam('id') || this.env.routing.getRouteParam('newsId'));
					if (this.isReady) void this.reloadAll();
				}
		);

		this.syncPreferencesFromRoute();
		this.state.routeCategoryId = this.normalizeCategoryId(this.env.routing.getRouteParam('categoryId'));
		this.state.translationId = this.normalizeCategoryId(this.env.routing.getRouteParam('id') || this.env.routing.getRouteParam('newsId'));
	}

	private syncPreferencesFromRoute(): void {
		const sourceParam = this.env.routing.getRouteParam('nativeLanguage');
		const targetParam = this.env.routing.getRouteParam('learnedLanguage');
		const modeParam = this.env.routing.getRouteParam('mode');

		const source = this.parseLanguage(sourceParam);
		const target = this.parseLanguage(targetParam);
		const mode = this.parseMode(modeParam);

		if (!source || !target || !mode) {
			void this.redirectWhenMissingPreferences();
			return;
		}

		this.state.nativeLanguage = source;
		this.state.learnedLanguage = target;
		this.state.mode = mode;

		this.normalizeRoute();
	}

	private normalizeRoute(): void {
		const sourceRaw = this.env.routing.getRouteParam('nativeLanguage');
		const targetRaw = this.env.routing.getRouteParam('learnedLanguage');
		const modeRaw = this.env.routing.getRouteParam('mode');
		const categoryId = this.env.routing.getRouteParam('categoryId') || undefined;
		const id = this.env.routing.getRouteParam('id') || this.env.routing.getRouteParam('newsId') || undefined;

		const normalizedSource = this.state.nativeLanguage;
		const normalizedTarget = this.state.learnedLanguage;
		const normalizedMode = this.state.mode;

		const shouldNormalize =
				(!!sourceRaw && sourceRaw!==normalizedSource) ||
				(!!targetRaw && targetRaw!==normalizedTarget) ||
				(!!modeRaw && modeRaw!==normalizedMode);

		if (!shouldNormalize) return;

		void this.env.routing.replace(
				this.env.routing.getArticleLink(
						normalizedSource,
						normalizedTarget,
						normalizedMode,
						categoryId || (this.state.selectedCategoryId || ''),
						id || ''
				)
		);
	}

	private async reloadAll(): Promise<void> {
		this.mnemonicFlowToken += 1;
		this.state.error = '';
		this.state.categories = [];
		this.state.article = null;
		this.state.selectedCategoryId = '';
		this.state.categoryTitleForHeader = '';
		this.state.articleParts = [];
		this.state.visiblePartCount = 0;
		this.state.isLoadingMnemonics = false;
		this.state.isMnemonicPopupVisible = false;
		this.state.isNavigatingToPrevious = false;
		this.state.isNavigatingToNext = false;
		this.state.hasPrevious = false;
		this.state.hasNext = false;

		await this.loadCategoriesAndArticle();
	}

	private async loadCategoriesAndArticle(): Promise<void> {
		const translationId = this.state.translationId;
		if (!translationId) {
			this.state.error = 'No article id';
			return;
		}

		try {
			if (!this.categoryStore) {
				this.categoryStore = await this.getCategoryStore();
			}
			if (!this.translationStore) {
				this.translationStore = await this.getTranslationStore();
			}

			this.state.isLoadingCategories = true;
			this.state.isLoadingArticle = true;
			this.recomputeLoadingState();

			let userId: string | undefined;
			try {
				await this.currentUser.initialize();
				userId = this.currentUser.getUserId() ?? undefined;
			} catch {
				userId = undefined;
			}

			const { sourceLang, targetLang } = this.resolveNewsLanguages();
			const [categoriesResponse, articleResponse] = await Promise.all([
				this.categoryStore.getNewsCategories(sourceLang, targetLang, this.state.mode, userId),
				this.translationStore.getNewsContent(translationId)
			]);

			this.state.categories = [
				...categoriesResponse.categories,
				...(categoriesResponse.userCategories ?? [])
			];
			this.state.article = articleResponse;
			this.recomputeNavigationState();

			const rawCategoryId = this.state.routeCategoryId ?? '';
			const articleCategoryId = this.state.article?.title.category ?? '';
			let effectiveCategoryId = rawCategoryId || articleCategoryId;

			if (effectiveCategoryId && !this.state.categories.some(c => c.id===effectiveCategoryId)) {
				effectiveCategoryId = this.state.categories[0]?.id ?? '';
			}
			if (!effectiveCategoryId && this.state.categories.length > 0) {
				effectiveCategoryId = this.state.categories[0].id;
			}

			this.state.selectedCategoryId = effectiveCategoryId;
			this.recomputeCategoryTitleForHeader();
			this.initializeArticleParts();

			if (effectiveCategoryId && effectiveCategoryId!==rawCategoryId) {
				await this.env.routing.replace(
						this.env.routing.getArticleLink(
								this.state.nativeLanguage,
								this.state.learnedLanguage,
								this.state.mode,
								effectiveCategoryId,
								translationId
						)
				);
			}

			// Premium tracking/paywall overlay (does NOT change article loading)
			const categoryId = this.env.routing.getRouteParam('categoryId');
			if (userId) {
				const premiumTracking = this.env.get(IPremiumTrackingService);
				void this.showPaywallIfNeeded(premiumTracking, userId, translationId, categoryId);
			}
			this.revealAllArticleParts();
		} catch (e) {
			this.state.error = e instanceof Error ? e.message:'Error loading article page';
		} finally {
			this.state.isLoadingCategories = false;
			this.state.isLoadingArticle = false;
			this.recomputeLoadingState();
		}
	}

	private initializeArticleParts(): void {
		const content = this.state.article?.content ?? [];
		const titleSentence = this.state.article?.title?.title;
		const titleWordCount = titleSentence
			? this.textSplitter.countTranslatedWords(titleSentence)
			: 0;

		const parts = this.textSplitter
			.splitBySentenceWordCount(content, titleWordCount);

		this.state.articleParts = parts;
		this.state.visiblePartCount = parts.length;
		this.state.isLoadingMnemonics = false;
		this.state.isMnemonicPopupVisible = false;
	}

	private revealAllArticleParts(): void {
		this.state.visiblePartCount = this.state.articleParts.length;
	}

	private async showMnemonicPopupForPart(
		partIndex: number,
		flowToken: number
	): Promise<void> {
		if (partIndex < 0 || partIndex >= this.state.articleParts.length) {
			return;
		}

		const words = this.extractPopupWordsForPart(partIndex);
		const wordIds = words.map((word) => word.wordId);
		if (words.length===0) {
			return;
		}

		try {
			this.state.isLoadingMnemonics = true;
			const mnemonicService = this.env.get(IMnemonicService);
			const response = await mnemonicService.getMnemonics({ wordIds });

			if (flowToken!==this.mnemonicFlowToken) {
				return;
			}

			const mnemonicsByWordId = new Map(response.items.map((item) => [item.wordId, item]));
			const popupItems = this.toPopupItems(words, mnemonicsByWordId);

			this.state.isMnemonicPopupVisible = true;
			const { sourceLang: mnemonicSourceLang, targetLang: mnemonicTargetLang } = this.resolveNewsLanguages();
			const popupResult = await this.env.popupService.showPopup(
				new MnemonicPopupViewModel(
					partIndex + 1,
					this.state.articleParts.length,
					mnemonicTargetLang,
					mnemonicSourceLang,
					this.state.nativeLanguage,
					popupItems
				)
			);
			void popupResult;

			if (flowToken!==this.mnemonicFlowToken) {
				return;
			}
		} catch (error) {
			this.env.log('Failed to load mnemonics', error);
		} finally {
			if (flowToken===this.mnemonicFlowToken) {
				this.state.isLoadingMnemonics = false;
				this.state.isMnemonicPopupVisible = false;
			}
		}
	}

	private extractPartWords(partContent: readonly SentenceTranslationData[]): ArticlePartWord[] {
		const words: ArticlePartWord[] = [];

		for (const sentence of partContent) {
			this.collectSentenceWords(sentence, words);
		}

		return Array.from(new Map(words.map((word) => [word.wordId, word])).values());
	}

	private extractPopupWordsForPart(partIndex: number): ArticlePartWord[] {
		const partWords = this.extractPartWords(this.state.articleParts[partIndex]);
		if (partIndex!==0) {
			return partWords;
		}

		const titleSentence = this.state.article?.title.title;
		if (!titleSentence) {
			return partWords;
		}

		const titleWords: ArticlePartWord[] = [];
		this.collectSentenceWords(titleSentence, titleWords);
		return Array.from(new Map([...titleWords, ...partWords].map((word) => [word.wordId, word])).values());
	}

	private collectSentenceWords(sentence: SentenceTranslationData, output: ArticlePartWord[]): void {
		for (const word of sentence.words) {
			if (!word.translation || !word.normalizedWordId) {
				continue;
			}

			const sourceWord = sentence.originalWords[word.wordIndex] ?? '';
			if (!sourceWord) {
				continue;
			}

			output.push({
				wordId: word.normalizedWordId,
				word: word.translation,
				transliteration: word.transliteration,
				translation: sourceWord,
				normalizedWord: word.normalizedWord,
				normalizedTranslation: word.normalizedWordTranslation,
			});
		}
	}

	private toPopupItems(
		words: readonly ArticlePartWord[],
		mnemonicsByWordId: ReadonlyMap<string, { description: string; imageUrl: string }>
	): MnemonicPopupItem[] {
		return words.map((word) => {
			const mnemonic = mnemonicsByWordId.get(word.wordId);
			return {
				wordId: word.wordId,
				word: word.word,
				transliteration: word.transliteration,
				translation: word.translation,
				normalizedWord: word.normalizedWord,
				normalizedTranslation: word.normalizedTranslation,
				mnemonicText: mnemonic?.description ?? '',
				imageUrl: mnemonic?.imageUrl ?? ''
			};
		});
	}

	private extractAllWords(content: SentenceTranslationData[]): ArticleWordPair[] {
		const words: ArticleWordPair[] = [];

		if (this.state.mode===TranslationMode.Partial)
			content.forEach(sentence => {
				sentence.words.filter(x => x.translation).forEach(word => {
					const original = sentence.originalWords[word.wordIndex];
					if (word.translation)
						words.push({
							original: word.translation!,
							translation: original!,
							transliteration: word.transliteration
						});
				});
			});

		const uniqueWords = Array.from(new Map(words.map(w => [w.original.toLowerCase(), w])).values());
		return uniqueWords.sort((a, b) => a.original.toLowerCase().localeCompare(b.original.toLowerCase()));
	}

	private normalizeCategoryId(value: string | null | undefined): string | undefined {
		if (!value) return undefined;
		const trimmed = value.trim();
		return trimmed ? trimmed:undefined;
	}

	private parseLanguage(value: string | null | undefined): Language | null {
		if (!value) return null;
		const normalized = value.toLowerCase();
		const supported = new Set<string>(Object.values(Language));
		return supported.has(normalized) ? (normalized as Language):null;
	}

	private parseMode(value: string | null | undefined): TranslationMode | null {
		if (!value) return null;
		const normalized = value.toLowerCase();
		if (normalized===TranslationMode.Full) return TranslationMode.Full;
		if (normalized===TranslationMode.Partial) return TranslationMode.Partial;
		return null;
	}

	private async redirectWhenMissingPreferences(): Promise<void> {
		try {
			await this.currentUser.initialize();
			const isAuthenticated = !!this.currentUser.getUserId();
			if (isAuthenticated) {
				await this.env.routing.replace(this.env.routing.getMySettingsLink());
			} else {
				await this.env.routing.replace(this.env.routing.getIntroLink());
			}
		} catch (e) {
			this.env.log('Failed to redirect when preferences are missing', e);
		}
	}

	private async ensureAuthenticated(): Promise<boolean> {
		await this.currentUser.initialize();
		if (!this.currentUser.getUserId()) {
			await this.env.routing.navigate(this.env.routing.getLoginLink());
			return false;
		}
		return true;
	}

	private recomputeLoadingState(): void {
		this.state.isLoading = this.state.isLoadingCategories || this.state.isLoadingArticle;
		if (this.state.isLoadingCategories) {
			this.state.loadingKind = 'categories';
			return;
		}
		if (this.state.isLoadingArticle) {
			this.state.loadingKind = 'article';
			return;
		}
		this.state.loadingKind = 'none';
	}

	private recomputeNavigationState(): void {
		this.state.hasPrevious = !!this.state.article?.previousTranslationId;
		this.state.hasNext = !!this.state.article?.nextTranslationId;
	}

	private recomputeCategoryTitleForHeader(): void {
		const selected = this.state.categories.find(c => c.id===this.state.selectedCategoryId);
		if (selected) {
			this.state.categoryTitleForHeader = this.env.localizedStringToText(selected.name);
			return;
		}

		const articleCategoryId = this.state.article?.title.category;
		if (articleCategoryId) {
			const fromArticle = this.state.categories.find(c => c.id===articleCategoryId);
			this.state.categoryTitleForHeader = this.env.localizedStringToText(fromArticle?.name);
			return;
		}

		this.state.categoryTitleForHeader = '';
	}

	private isPaywallTrackedCategory(categoryId: string | null | undefined): boolean {
		if (!categoryId) {
			return true;
		}

		return categoryId!==DefaultNewsCategoryId.World &&
			categoryId!==DefaultNewsCategoryId.Entertainment &&
			categoryId!==DefaultNewsCategoryId.Technology;
	}

	private async showPaywallIfNeeded(
			premiumTracking: IPremiumTrackingService,
			userId: string,
			translationId: string,
			categoryId: string | null | undefined
	): Promise<void> {

		if (!this.isPaywallTrackedCategory(categoryId)) {
			return;
		}
		try {
			// Prevent spamming the same paywall popup while reloading the same article.
			if (this.paywallShownForTranslationId===translationId) {
				void premiumTracking.trackArticleView(userId);
				return;
			}

			const available = await premiumTracking.isArticleAvailable(userId);
			await premiumTracking.trackArticleView(userId);

			if (available) {
				return;
			}

			this.paywallShownForTranslationId = translationId;

			const popupVm = new PaywallPopupViewModel({
				message: { stringId: 'errors.paywall.membershipRequired', formatParams: {} },
				buyMeCoffeeUrl: 'https://buymeacoffee.com/lingvo.news/membership',
				useTelegramPayment: this.useTelegramPayment,
			});

			let pollIntervalId: ReturnType<typeof setInterval> | null = null;
			const stopPolling = (): void => {
				if (pollIntervalId) {
					clearInterval(pollIntervalId);
					pollIntervalId = null;
				}
			};

			// Poll membership status and close the paywall once premium becomes active.
			pollIntervalId = setInterval(() => {
				void (async () => {
					try {
						await this.currentUser.refresh();
						const nowAvailable = await premiumTracking.isArticleAvailable(userId);
						if (nowAvailable) {
							stopPolling();
							popupVm.closeAfterPurchase();
						}
					} catch (e) {
						this.env.log('Error polling membership status', e);
					}
				})();
			}, 3000);

			try {
				const result = await this.env.popupService.showPopup(popupVm);
				if (result.isCanceled) {
					await this.env.routing.navigate(
							this.env.routing.getNewsListLink(
									this.state.nativeLanguage,
									this.state.learnedLanguage,
									this.state.mode,
									this.state.selectedCategoryId || undefined
							)
					);
				}
			} finally {
				stopPolling();
			}
		} catch (e) {
			this.env.log('Premium tracking failed', e);
		}
	}

	public format(): string {
		const articleLoaded = this.state.article!==null;
		const hasCategories = this.state.categories.length > 0;
		const headerTitleSet = this.state.categoryTitleForHeader.trim().length > 0;
		const hasError = this.state.error.trim().length > 0;

		return [
			'ArticlePageViewModel',
			`translationId: ${this.state.translationId ?? ''}`,
			`routeCategoryId: ${this.state.routeCategoryId ?? ''}`,
			`articleLoaded: ${articleLoaded}`,
			`articleTitleTranslationId: ${this.state.article?.title.translationId ?? ''}`,
			`hasCategories: ${hasCategories}`,
			`selectedCategoryId: ${this.state.selectedCategoryId}`,
			`headerTitleSet: ${headerTitleSet}`,
			`hasPrevious: ${this.state.hasPrevious}`,
			`hasNext: ${this.state.hasNext}`,
			`isLoading: ${this.state.isLoading}`,
			`loadingKind: ${this.state.loadingKind}`,
			`hasError: ${hasError}`,
			`error: ${hasError ? '<non-empty>':''}`
		].join('\n');
	}
}
